import { vec3 } from "gl-matrix";
import Cache from "core/cache/index";
import math from "core/math";
import { BaseAnnotationTool } from "./base/BaseAnnotationTool.js";

import { moveAllHandles } from "../../core/manipulators/index.js";
// State
import {
  addToolState,
  getToolState,
  clearToolState,
} from "component/toolManager/toolState.js";
import {
  getEnabledElements,
  getEnabledElement,
} from "component/toolManager/enabledElements.js";
import { state } from "component/molecules/viewport/toolStates/store.js";
// Drawing
import {
  getNewContext,
  draw,
  drawHandles,
  drawHandle,
} from "./../../draw/index.js";

import makeKey from "core/cache/KeyGen.js";
//import renderActiveReferenceLine from "./referencelines/renderActiveReferenceLine.js";
import renderActiveReferenceLine from "./crosshairReferenceLine/renderReferenceLine";
import getRenderReferenceLine from "./crosshairReferenceLine/getRenderReferenceLine";
import _ from "lodash";
import {
  imagePointToPatientPoint,
  projectPatientPointToImagePlane,
} from "core/math/transform/projection.js";
import { canvasToPixel } from "core/math/coordinate/coordinate";
/**
 * @public
 * @class EllipseRoiTool
 * @memberof Tools.Annotation
 * @classdesc Tool for drawing rectangular regions of interest, and measuring
 * the statistics of the enclosed pixels.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class Crosshair3DTool extends BaseAnnotationTool {
  constructor(configuration = {}) {
    const defaultConfig = {
      name: "crosshair3D",
      mixins: ["activeOrDisabledBinaryTool"],
      supportedInteractionTypes: ["Mouse", "Touch"],
      options: {
        // mouseButtonMask: 1,
        preventHandleOutsideImage: true,
      },
      configuration: {
        shadow: true,
        shadowColor: "#000000",
        shadowOffsetX: 1,
        shadowOffsetY: 1,
        rotationAxis: "Y",
      },
    };
    const initialConfiguration = Object.assign(defaultConfig, configuration);

    super(initialConfiguration);
    this.initialConfiguration = initialConfiguration;
    this.mergeOptions(initialConfiguration.options);
  }
  createNewMeasurement(evt) {}
  activeCallback(element, options) {
    clearToolState(this.element, this.name);
    addToolState(this.element, this.name, {
      visible: true,
      active: true,
      color: options.color,
      initCosines: options.cosines,
      appliedAngleRadians: 0,
      handles: {
        start: {
          x: 0,
          y: 0,
          highlight: true,
          active: true,
        },
        end: {
          x: 0,
          y: 0,
          highlight: true,
          active: true,
        },
      },
      crossPoint: { x: 0, y: 0 },
    });
  }

  pointNearHandle(element, data, coords) {
    if (data.visible === false) {
      return false;
    }

    const isNear = false;
    const handles = data.handles;
    const handleKeys = Object.keys(data.handles);
    for (let i = 0; i < handleKeys.length; i++) {
      const handle = handles[handleKeys[i]];
      const dist = math.point.distance(handle, coords);
      if (dist < 10) {
        isNear = isNear | true;
      } else {
        isNear = isNear | false;
      }
    }
    return isNear;
  }

  pointNearTool(element, data, coords) {
    if (data.visible === false) {
      return false;
    }
    return (
      math.line.lineSegemntDistance(
        element,
        data.handles.start,
        data.handles.end,
        coords
      ) < 25
    );
  }
  mouseUpCallback(evt) {
    const element = evt.srcElement;
    const enabledElement = getEnabledElement(element);
    const image = enabledElement.image;
    const value = evt.detail.currentPoints.image;
    if (this.options.preventHandleOutsideImage) {
      math.range.clipToBox(value, image);

      value.x = math.max(math.min(value.x, value.x - 1), 0);
      value.y = math.max(math.min(value.y, value.y - 1), 0);
    }
    /**
     * 1. 클릭된 좌표와 Handle 교차점 사이의 좌표 변위 계산
     * (이미지 좌표계 상에서 연산)
     */
    const currentPointInImage = evt.detail.currentPoints.image;
    const toolData = getToolState(evt.currentTarget, this.name);
    const crossPoint = toolData.data[0].crossPoint;
    if (!!crossPoint === true) {
      // cross point 와 이미지 클릭 위치가 동일하다면, Image Position Patient
      // 이동을 하지 않고 종료
      if (
        Math.floor(currentPointInImage.x) === Math.floor(crossPoint.x) &&
        Math.floor(currentPointInImage.y) === Math.floor(crossPoint.y)
      ) {
        //console.log("skip move");
        return;
      }
    }

    let delta = {
      x: 0,
      y: 0,
    };

    const handles = toolData.data[0].handles;
    const handleKeys = Object.keys(handles);
    for (let i = 0; i < handleKeys.length; i++) {
      const key = handleKeys[i];
      const handle = handles[key];

      const diff = {
        x: currentPointInImage.x - handle.x,
        y: currentPointInImage.y - handle.y,
      };
      delta = {
        x: delta.x + diff.x,
        y: delta.y + diff.y,
      };
    }

    /**
     * 2. 1에서 획득한 좌표 변위를 Patient 좌표계로 변환
     */

    const imagePlane = {
      imagePositionPatient: image.imagePosition,
      rowCosines: image.rowCosines,
      columnCosines: image.columnCosines,
      frameOfReferenceUID: image.frameOfReferenceUID,
      rows: image.rows,
      columns: image.columns,
      rowPixelSpacing: image.pixelSpacing[0],
      columnPixelSpacing: image.pixelSpacing[1],
    };
    const originPatientPoint = math.Projection.imagePointToPatientPoint(
      { x: 0, y: 0 },
      imagePlane
    );
    const deltaPatientPoint = math.Projection.imagePointToPatientPoint(
      delta,
      imagePlane
    );
    const diffPatientPoint = {
      x: deltaPatientPoint.x - originPatientPoint.x,
      y: deltaPatientPoint.y - originPatientPoint.y,
      z: deltaPatientPoint.z - originPatientPoint.z,
    };

    /**
     * 3. 변위 만큼 각 뷰의 Image Position Patient 값을 수정
     */
    const allEnabledElements = getEnabledElements();
    for (let i = 0; i < allEnabledElements.length; i++) {
      const enabledElement = allEnabledElements[i];
      const image = enabledElement.image;
      if (!!image === false) {
        continue;
      }
      const imageOrientation =
        image.imageOrientation || image.imageOrientationPatient;
      image.width = image.columns;
      image.height = image.rows;
      const iop = imageOrientation;
      const ipp = _.cloneDeep(image.imagePosition);
      const rowCosines = vec3.fromValues(iop[0], iop[1], iop[2]);
      const colCosines = vec3.fromValues(iop[3], iop[4], iop[5]);
      let zedCosines = vec3.create();

      vec3.cross(zedCosines, rowCosines, colCosines);

      //Update position in the Zed direction
      const stepSize = 1;
      const dir = 1;
      let dx = zedCosines[0] * stepSize * (dir * -1) * diffPatientPoint.x;
      let dy = zedCosines[1] * stepSize * dir * diffPatientPoint.y;
      let dz = zedCosines[2] * stepSize * dir * diffPatientPoint.z;
      // 2. 변경된 imagePositionPatient 정보를 업데이트
      ipp[0] += dx;
      ipp[1] += dy;
      ipp[2] += dz;

      image.imagePosition[0] = ipp[0];
      image.imagePosition[1] = ipp[1];
      image.imagePosition[2] = ipp[2];
    }
    this.updatePoint(evt);
  }
  mouseMoveCallback(evt) {
    this.updatePoint(evt);
    super.mouseMoveCallback(evt);
  }

  /**
   * MPR ImagePosition 변경
   */
  toolSelectedCallback(evt, annotation, interactionType = "mouse") {
    //console.log("call ToolSelected");
    const toolOptions = Object.assign(
      {},
      {
        doneMovingCallback: () => {
          annotation.active = false;
          state.isToolLocked = false;
        },
        movingCallback: (delta) => {
          const stingIndex = ["start", "end"];
          const element = evt.srcElement;
          const enabledElement = getEnabledElement(element);
          // 1. imagePositionPatient를 delta 만큼 이동
          // delta 정보를 이미지 좌표계 -> Patient 좌표계로 변경

          const image = enabledElement.image;
          const imagePlane = {
            imagePositionPatient: image.imagePosition,
            rowCosines: image.rowCosines,
            columnCosines: image.columnCosines,
            frameOfReferenceUID: image.frameOfReferenceUID,
            rows: image.rows,
            columns: image.columns,
            rowPixelSpacing: image.pixelSpacing[0],
            columnPixelSpacing: image.pixelSpacing[1],
          };
          const originPatientPoint = math.Projection.imagePointToPatientPoint(
            { x: 0, y: 0 },
            imagePlane
          );

          const deltaPatientPoint = math.Projection.imagePointToPatientPoint(
            delta,
            imagePlane
          );

          const diffPatientPoint = {
            x: deltaPatientPoint.x - originPatientPoint.x,
            y: deltaPatientPoint.y - originPatientPoint.y,
            z: deltaPatientPoint.z - originPatientPoint.z,
          };

          let metadataCache = new Cache.Metadata();
          const testKey = makeKey(
            enabledElement.image.seriesInstanceUID,
            enabledElement.image.modality
          );
          const bounds = metadataCache.getImagePatientPositionBounds(testKey);
          // imagePositionPatient에 delta 정보 반영
          const allEnabledElements = getEnabledElements();
          for (let i = 0; i < allEnabledElements.length; i++) {
            const enabledElement = allEnabledElements[i];
            const image = enabledElement.image;
            if (!!image === false) {
              continue;
            }
            let targetElement = enabledElement.element;
            let targetImage = enabledElement.image;
            const targetImagePlane = {
              imagePositionPatient: targetImage.imagePosition,
              rowCosines: targetImage.rowCosines,
              columnCosines: targetImage.columnCosines,
              frameOfReferenceUID: targetImage.frameOfReferenceUID,
              rows: targetImage.rows,
              columns: targetImage.columns,
              rowPixelSpacing: targetImage.pixelSpacing[0],
              columnPixelSpacing: targetImage.pixelSpacing[1],
            };

            const imageOrientation =
              image.imageOrientation || image.imageOrientationPatient;
            image.width = image.columns;
            image.height = image.rows;
            const iop = imageOrientation;
            const ipp = _.cloneDeep(image.imagePosition);
            const rowCosines = vec3.fromValues(iop[0], iop[1], iop[2]);
            const colCosines = vec3.fromValues(iop[3], iop[4], iop[5]);
            let zedCosines = vec3.create();

            vec3.cross(zedCosines, rowCosines, colCosines);

            //Update position in the Zed direction
            const stepSize = 1;
            const dir = 1;
            let dx = zedCosines[0] * stepSize * (dir * -1) * diffPatientPoint.x;
            let dy = zedCosines[1] * stepSize * dir * diffPatientPoint.y;
            let dz = zedCosines[2] * stepSize * dir * diffPatientPoint.z;
            // 2. 변경된 imagePositionPatient 정보를 업데이트
            ipp[0] += dx;
            ipp[1] += dy;
            ipp[2] += dz;

            image.imagePosition[0] = math.min(
              math.max(ipp[0], bounds[0]),
              bounds[1]
            );
            image.imagePosition[1] = math.min(
              math.max(ipp[1], bounds[2]),
              bounds[3]
            );
            image.imagePosition[2] = math.min(
              math.max(ipp[2], bounds[4]),
              bounds[5]
            );

            // 갱신된 Handle의 위치를 다른 뷰에도 위치 동기화
            const toolData = getToolState(element, this.name);
            const handles = toolData.data[0].handles;
            const handleKeys = Object.keys(handles);
            for (let i = 0; i < handleKeys.length; i++) {
              const handle = handles[handleKeys[i]];
              // handle 좌표를 Patient 좌표계로 변환
              const handlePatientPoint = imagePointToPatientPoint(
                { x: handle.x, y: handle.y },
                imagePlane
              );
              // handle 좌표를 Target Element의 Image 좌표계로 변환
              const handlePointInTarget = projectPatientPointToImagePlane(
                handlePatientPoint,
                targetImagePlane
              );
              // target element의 tool data 획득
              const toolData = getToolState(targetElement, this.name);

              if (!toolData) {
                continue;
              }
              toolData.data[0].handles[`${stingIndex[i]}`].x =
                handlePointInTarget.x;
              toolData.data[0].handles[`${stingIndex[i]}`].y =
                handlePointInTarget.y;
            }
          }
        },
      },
      this.options
    );
    //super.toolSelectedCallback(evt, annotation, interactionType);
    annotation.active = true;
    state.isToolLocked = true;

    moveAllHandles(
      evt.detail,
      this.name,
      annotation,
      null,
      toolOptions,
      interactionType
    );

    evt.stopImmediatePropagation();
    evt.stopPropagation();
    evt.preventDefault();

    return;
  }

  handleSelectedCallback(evt, toolData, handle, interactionType = "mouse") {}

  closestPointToPointOnLine(p, a, b) {
    var atob = { x: b.x - a.x, y: b.y - a.y };
    var atop = { x: p.x - a.x, y: p.y - a.y };
    var len = atob.x * atob.x + atob.y * atob.y;
    var dot = atop.x * atob.x + atop.y * atob.y;
    var t = math.min(1, math.max(0, dot / len));

    dot = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);

    return {
      point: {
        x: a.x + atob.x * t,
        y: a.y + atob.y * t,
      },
      left: dot < 1,
      dot: dot,
      t: t,
    };
  }

  updatePoint(evt) {
    const eventData = evt.detail;
    evt.stopImmediatePropagation();

    const element = evt.currentTarget;
    const enabledElement = getEnabledElement(element);

    if (!!_.get(enabledElement, "image", null) === false) {
      return;
    }

    const image = enabledElement.image;
    const imagePlane = {
      imagePositionPatient: image.imagePosition,
      rowCosines: image.rowCosines,
      columnCosines: image.columnCosines,
      frameOfReferenceUID: image.frameOfReferenceUID,
      rows: image.rows,
      columns: image.columns,
      rowPixelSpacing: image.pixelSpacing[0],
      columnPixelSpacing: image.pixelSpacing[1],
    };
    // 이미지 좌표계에서 마우스 좌표정보 획득
    const imagePointXY = eventData.currentPoints.image;

    const ipp = math.Projection.imagePointToPatientPoint(
      imagePointXY,
      imagePlane
    );
    // 축 과 마우스 X/Y 평면과 교차점 계산
    const ippCross = math.Projection.imagePointToPatientPoint(
      imagePointXY,
      imagePlane
    );
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }
    /**
     * 핸들을 현재 뷰에 출력후 -> 다른 뷰에 위치 업데이트
     */
    const allEnabledElements = getEnabledElements();
    let i = 0;
    const stingIndex = ["start", "end"];
    let referenceLines = [];
    allEnabledElements.forEach((targetEnabledElement) => {
      // Annotation 핸들을 현재 뷰에 출력
      if (!!_.get(targetEnabledElement, "element", null) === false) {
        return;
      }

      const targetElement = targetEnabledElement.element;
      if (!!_.get(targetEnabledElement, "image", null) === false) {
        return;
      }
      if (element === targetElement) {
        return;
      }

      let targetImage = targetEnabledElement.image;
      const targetImagePlane = {
        imagePositionPatient: targetImage.imagePosition,
        rowCosines: targetImage.rowCosines,
        columnCosines: targetImage.columnCosines,
        frameOfReferenceUID: targetImage.frameOfReferenceUID,
        rows: targetImage.rows,
        columns: targetImage.columns,
        rowPixelSpacing: targetImage.pixelSpacing[0],
        columnPixelSpacing: targetImage.pixelSpacing[1],
      };

      let referenceLine = getRenderReferenceLine(
        element,
        imagePlane,
        targetImagePlane
      );
      if (!!referenceLine === false) {
        return;
      }
      // Canvas 좌표계에서 마우스 X/Y 축과 ReferenceLine의 교차점 검색

      const canvasPointXY = math.coordinate.pixelToCanvas(
        element,
        imagePointXY
      );
      const crossPoint = this.closestPointToPointOnLine(
        canvasPointXY,
        referenceLine.start,
        referenceLine.end
      );
      // crossPoint를 이미지 좌표계로 변환
      const crossPointInImage = math.coordinate.canvasToPixel(
        element,
        crossPoint.point
      );
      toolData.data[0].crossPoint = crossPointInImage;

      toolData.data[0].handles[`${stingIndex[i]}`].x = crossPointInImage.x;
      toolData.data[0].handles[`${stingIndex[i]}`].y = crossPointInImage.y;

      // 갱신된 Handle의 위치를 다른 뷰에도 위치 동기화
      const handles = toolData.data[0].handles;
      const handleKeys = Object.keys(handles);
      for (let i = 0; i < handleKeys.length; i++) {
        const handle = handles[handleKeys[i]];
        // handle 좌표를 Patient 좌표계로 변환
        const handlePatientPoint = imagePointToPatientPoint(
          { x: handle.x, y: handle.y },
          imagePlane
        );
        // handle 좌표를 Target Element의 Image 좌표계로 변환
        const handlePointInTarget = projectPatientPointToImagePlane(
          handlePatientPoint,
          targetImagePlane
        );
        // target element의 tool data 획득
        const toolData = getToolState(targetElement, this.name);

        if (!toolData) {
          continue;
        }
        toolData.data[0].handles[`${stingIndex[i]}`].x = handlePointInTarget.x;
        toolData.data[0].handles[`${stingIndex[i]}`].y = handlePointInTarget.y;
      }
      targetEnabledElement.needsRedraw = true;
      // 뷰 업데이트

      i++;
    });

    enabledElement.needsRedraw = true;
  }

  renderToolData(evt) {
    const eventData = evt.detail;
    let { image, element } = eventData;
    const sourceEnabledElement = eventData;
    //if (!image || !image.pixelSpacing) {
    if (!image) {
      return;
    }
    const toolData = getToolState(evt.currentTarget, this.name);
    if (!!toolData === false) {
      return;
    }
    //return;
    const context = getNewContext(eventData.canvas);

    let metadataCache = new Cache.Metadata();

    const sourceDataID = makeKey(
      sourceEnabledElement.image.seriesInstanceUID,
      sourceEnabledElement.image.modality
    );
    const sourceStudyUID = metadataCache.getStudyInstanceUID(sourceDataID);
    let enabledElements = getEnabledElements();
    let referenceLines = [];
    // 각 화면에 출력되는 뷰의 축 평면의 위치를 가이딩하는 선 그리기
    for (let i = 0; i < enabledElements.length; i++) {
      const targetEnabledElement = enabledElements[i];
      if (
        !!_.get(targetEnabledElement, "image.seriesInstanceUID", null) === false
      ) {
        continue;
      }
      const targetDataID = makeKey(
        targetEnabledElement.image.seriesInstanceUID,
        targetEnabledElement.image.modality
      );

      const targetStudyUID = metadataCache.getStudyInstanceUID(targetDataID);

      if (
        sourceStudyUID !== targetStudyUID ||
        sourceEnabledElement.element === targetEnabledElement.element
      ) {
        continue;
      }
      const targetImage = targetEnabledElement.image;
      const referenceImage = sourceEnabledElement.image;

      const targetImagePlane = {
        imagePositionPatient: targetImage.imagePosition,
        rowCosines: targetImage.rowCosines,
        columnCosines: targetImage.columnCosines,
        frameOfReferenceUID: targetImage.frameOfReferenceUID,
        rows: targetImage.rows,
        columns: targetImage.columns,
        rowPixelSpacing: targetImage.pixelSpacing[0],
        columnPixelSpacing: targetImage.pixelSpacing[1],
      };

      const referenceImagePlane = {
        imagePositionPatient: referenceImage.imagePosition,
        rowCosines: referenceImage.rowCosines,
        columnCosines: referenceImage.columnCosines,
        frameOfReferenceUID: referenceImage.frameOfReferenceUID,
        rows: referenceImage.rows,
        columns: referenceImage.columns,
        rowPixelSpacing: referenceImage.pixelSpacing[0],
        columnPixelSpacing: referenceImage.pixelSpacing[1],
      };

      // renderRefrenceLine
      const data = toolData.data[0];
      let handles = null;
      if (!!data === false) {
      } else {
        const handleKeys = Object.keys(data.handles);
        handles = handleKeys.map((key) => {
          return { x: data.handles[key].x, y: data.handles[key].y };
        });
      }

      const drawColor = targetImage.mprColor || "#00ff00";
      const referenceLine = renderActiveReferenceLine(
        context,
        sourceEnabledElement.element,
        referenceImagePlane,
        targetImagePlane,
        { drawColor, handles }
      );
      targetEnabledElement.needsRedraw = true;
      if (!!referenceLine === false) {
        return;
      }

      const referenceLineInImage = {
        start: canvasToPixel(element, referenceLine.start),
        end: canvasToPixel(element, referenceLine.end),
      };
      referenceLines.push({
        line: referenceLineInImage,
        color: drawColor,
      });
      if (!!referenceLine === false) {
        return;
      }
    }

    if (referenceLines.length === 2) {
      const crossPoint = math.line.intersectLine(
        referenceLines[0].line,
        referenceLines[1].line
      );
      toolData.data[0].crossPoint = crossPoint;
    }

    // Annotation 도구 변형 핸들 그리기
    draw(context, (context) => {
      // If we have tool data for this element - iterate over each set and draw it
      for (let i = 0; i < toolData.data.length; i++) {
        const data = toolData.data[i];
        const handleKeys = Object.keys(data.handles);
        for (let j = 0; j < handleKeys.length; j++) {
          // Configure the handles
          let handleOptions = {
            handleRadius: 5,
            drawHandlesIfActive: false,
          };

          const handle = data.handles[handleKeys[j]];
          // get color
          let color = null;
          let candidateDist = 99999;
          if (
            !!_.get(data, "crossPoint.x") === false ||
            !!_.get(handle, "x") === false
          ) {
            continue;
          }
          const distFromCrossPoint = math.point.distance(
            data.crossPoint,
            handle
          );
          if (distFromCrossPoint < 0.0001) {
            continue;
          }
          for (let k = 0; k < referenceLines.length; k++) {
            const line = referenceLines[k].line;
            const dist = math.line.distanceToPoint(line, handle);

            //if (dist < candidateDist) {
            if (dist < candidateDist) {
              candidateDist = dist;
              color = referenceLines[k].color;
            }
          }
          if (!!color === true) {
            handleOptions.color = color;
            drawHandle(context, eventData, handle, handleOptions);
          }
        }
      }
    });
  }
}
